import { AnimatePresence, motion } from 'motion/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import { ModularCard } from '@/components/ui/custom/modular/ModularCard';
import useToggle from '@/data/hook/useToggle';
import { TempObjective } from '@/models/CoursePath';
import { bodyTextSmall } from '@/styles/styles';
import { errorMessage } from '@/utils/animations/commom';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';
import { PencilIcon } from '@heroicons/react/24/outline';

type Props = {
  objectives?: TempObjective[];
  hasOpen?: boolean;
  onEdit?: () => void;
  className?: string;
  render?: boolean;
};

export default function CourseStudioObjectives(props: Props) {
  const {
    objectives = [],
    hasOpen,
    onEdit,
    className,
    render = objectives.length > 0,
  } = props;

  const { isOpen, toggle, open } = useToggle(hasOpen);

  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  useEffect(() => {
    if (hasOpen) open();
  }, [open, hasOpen]);

  const filteredObjectives = objectives.filter(objective => objective.name);

  return (
    <AnimatePresence>
      {render && (
        <motion.div
          className={twJoin(isOpen && className)}
          style={
            isOpen
              ? {
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }
              : undefined
          }
          {...errorMessage}
        >
          <ModularCard
            className={{ base: twJoin('flex flex-col', isOpen && 'h-full') }}
          >
            <ModularCard.Header
              title={t('objectives')}
              isOpen={isOpen}
              onOpen={toggle}
              className={{ title: 'text-primary', base: 'shrink-0' }}
            >
              <ConditionalRenderer condition={onEdit}>
                <IconButton
                  icon={<PencilIcon className="w-4 h-4 text-primary" />}
                  onClick={onEdit}
                  tooltip={t('edit')}
                />
              </ConditionalRenderer>
            </ModularCard.Header>

            <ModularCard.Accordion
              isOpen={isOpen}
              className={{ base: 'flex flex-col overflow-hidden' }}
            >
              <ul
                className={twJoin(
                  'flex flex-col gap-2 p-4 pt-0 list-disc pl-10',
                  isOpen && 'overflow-y-auto max-h-[calc(100vh-22rem)]',
                  scrollBarYClassName,
                )}
              >
                <ConditionalRenderer condition={!filteredObjectives.length}>
                  <p
                    className={twJoin(
                      bodyTextSmall,
                      'relative text-neutral text-center mt-2',
                    )}
                  >
                    {t('noResults')}
                  </p>
                </ConditionalRenderer>
                {filteredObjectives.map(objective => (
                  <li
                    key={objective.tempId || objective.id}
                    className="text-gray-700"
                  >
                    <p className={twJoin(bodyTextSmall, 'text-neutral')}>
                      {objective.name}
                    </p>
                  </li>
                ))}
              </ul>
            </ModularCard.Accordion>
          </ModularCard>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
